/**
 * E3 — o cartão de ação pendente.
 *
 * No C9 (dossiê de 15/08/2026) o agente reafirmou colaborador, dia e horário em
 * PROSA e pediu confirmação. A pessoa respondeu "sim" e foi abandonada. Prosa
 * de confirmação tem dois defeitos que um cartão não tem: ela não diz o que o
 * "sim" vai fazer, e ela obriga a pessoa a digitar a confirmação num canal em
 * que qualquer palavra a mais muda o rumo do turno.
 *
 * Este módulo é a REGRA, e só ela: de um envelope `formulario` já completo, o
 * par capacidade + parâmetros por rótulo. Quem desenha é o SFC; quem despacha é
 * o shell, com a action `gerarPrevia` que já existe.
 *
 * **Nenhum campo de wire novo.** O cartão nasce do envelope que o backend já
 * publica — é a mesma informação, apresentada onde a decisão acontece.
 */

/** Um parâmetro como a pessoa o reconhece: pelo rótulo, nunca pelo `nome`. */
export interface ParametroDaAcaoPendente {
  nome: string
  rotulo: string
  valor: string
}

export interface AcaoPendente {
  capacidade: string
  parametros: ParametroDaAcaoPendente[]
  /** Payload pronto para a action `gerarPrevia` — `{ capability, input }`. */
  valores: Record<string, string>
}

/** Forma mínima do envelope `formulario` que esta regra consome. */
export interface EnvelopeDeFormulario {
  tipo?: string
  capacidade?: string
  campos?: Array<{ nome?: string, rotulo?: string, obrigatorio?: boolean }>
  valores?: Array<{ nome?: string, valor?: string }>
}

/**
 * Texto canônico do cancelamento. É o MESMO que sobe pelo `send` existente, de
 * modo que o botão e a digitação percorram exatamente o mesmo caminho no
 * backend — o léxico de cancelamento do servidor reconhece esta palavra.
 */
export const TEXTO_DE_CANCELAMENTO = 'cancelar'

/**
 * Deriva o cartão de um envelope `formulario` COMPLETO.
 *
 * Formulário incompleto devolve `null` de propósito: ali a coleta ainda está
 * acontecendo e o veículo continua sendo o `CanvasActionForm`, onde a pessoa
 * digita o que falta. Um cartão "Confirmar" com campo vazio seria um botão que
 * promete o que não pode cumprir.
 */
export function cartaoDeAcaoPendente (envelope: EnvelopeDeFormulario | null | undefined): AcaoPendente | null {
  if (!envelope || envelope.tipo !== 'formulario') return null
  const capacidade = (envelope.capacidade || '').trim()
  if (!capacidade) return null

  const campos = envelope.campos || []
  if (!campos.length) return null

  const valoresPorNome = new Map<string, string>()
  for (const item of envelope.valores || []) {
    if (typeof item?.nome === 'string' && typeof item?.valor === 'string') {
      valoresPorNome.set(item.nome, item.valor)
    }
  }

  const parametros: ParametroDaAcaoPendente[] = []
  const valores: Record<string, string> = {}
  for (const campo of campos) {
    const nome = (campo?.nome || '').trim()
    if (!nome) return null
    const valor = (valoresPorNome.get(nome) || '').trim()
    if (campo?.obrigatorio && !valor) return null
    if (!valor) continue
    parametros.push({ nome, rotulo: (campo?.rotulo || '').trim() || nome, valor })
    valores[nome] = valor
  }

  if (!parametros.length) return null
  return { capacidade, parametros, valores }
}
